import { memo, useCallback, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { alpha, useTheme } from '@mui/material/styles';
import { buildTocTree } from '../../markdown/toc';
import { useActiveHeading } from '../../hooks/useActiveHeading';
import type { Heading, TocNode } from '../../types';

export interface TableOfContentsProps {
  headings: Heading[];
  /** Shows the filter box; helpful for very long documents. */
  filterable?: boolean;
  onNavigate?: (id: string) => void;
  title?: string;
}

const matches = (node: TocNode, query: string): boolean =>
  node.text.toLowerCase().includes(query) || node.children.some((child) => matches(child, query));

/**
 * Hierarchical, collapsible table of contents with active-section tracking.
 *
 * Renders nothing when the document has no headings so the layout does not
 * reserve space for an empty panel.
 */
export const TableOfContents = memo(function TableOfContents({
  headings,
  filterable = true,
  onNavigate,
  title = 'Table of Contents',
}: TableOfContentsProps) {
  const theme = useTheme();
  const [query, setQuery] = useState('');
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const activeId = useActiveHeading(headings);

  const tree = useMemo(() => buildTocTree(headings), [headings]);
  const normalizedQuery = query.trim().toLowerCase();

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>, id: string) => {
      event.preventDefault();
      const target = document.getElementById(id);
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        window.scrollTo(0, 0);
      }
      window.history.pushState(null, '', `#${id}`);
      onNavigate?.(id);
    },
    [onNavigate],
  );

  const toggle = useCallback((id: string) => {
    setCollapsed((state) => ({ ...state, [id]: !state[id] }));
  }, []);

  if (headings.length === 0) return null;

  const renderNodes = (nodes: TocNode[], depth = 0) => (
    <List dense disablePadding component="ul" sx={{ pl: depth === 0 ? 0 : 1.25 }}>
      {nodes
        .filter((node) => normalizedQuery === '' || matches(node, normalizedQuery))
        .map((node) => {
          const isActive = node.id === activeId;
          const isCollapsed = collapsed[node.id] ?? false;
          const hasChildren = node.children.length > 0;

          return (
            <Box component="li" key={node.id} sx={{ listStyle: 'none' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ListItemButton
                  component="a"
                  href={`#${node.id}`}
                  onClick={(event: React.MouseEvent<HTMLAnchorElement>) => handleClick(event, node.id)}
                  selected={isActive}
                  aria-current={isActive ? 'location' : undefined}
                  sx={{
                    py: 0.35,
                    pl: 1,
                    minWidth: 0,
                    flex: 1,
                    borderLeft: `2px solid ${isActive ? theme.palette.primary.main : 'transparent'}`,
                    borderRadius: '0 8px 8px 0',
                    transition: theme.transitions.create(['background-color', 'border-color'], {
                      duration: 140,
                    }),
                    '&.Mui-selected': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.16) },
                    },
                  }}
                >
                  <ListItemText
                    primary={node.text}
                    primaryTypographyProps={{
                      variant: 'body2',
                      noWrap: true,
                      title: node.text,
                      sx: {
                        fontWeight: isActive ? 700 : node.level <= 2 ? 600 : 400,
                        color: isActive ? 'primary.main' : 'text.secondary',
                        fontSize: node.level <= 2 ? '0.875rem' : '0.82rem',
                      },
                    }}
                  />
                </ListItemButton>

                {hasChildren && (
                  <IconButton
                    size="small"
                    aria-label={isCollapsed ? `Expand ${node.text}` : `Collapse ${node.text}`}
                    aria-expanded={!isCollapsed}
                    onClick={() => toggle(node.id)}
                    sx={{ p: 0.25, mr: 0.5 }}
                  >
                    <ExpandMoreRoundedIcon
                      fontSize="small"
                      sx={{
                        transition: theme.transitions.create('transform', { duration: 150 }),
                        transform: isCollapsed ? 'rotate(-90deg)' : 'none',
                      }}
                    />
                  </IconButton>
                )}
              </Box>

              {hasChildren && (
                <Collapse in={!isCollapsed} timeout="auto" unmountOnExit>
                  {renderNodes(node.children, depth + 1)}
                </Collapse>
              )}
            </Box>
          );
        })}
    </List>
  );

  return (
    <Box
      component="nav"
      className="table-of-contents"
      aria-label={title}
      sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%' }}
    >
      <Typography
        variant="overline"
        sx={{ px: 2, pt: 2, pb: 1, fontWeight: 700, letterSpacing: '0.08em', color: 'text.secondary' }}
      >
        {title}
      </Typography>

      {filterable && headings.length > 8 && (
        <Box sx={{ px: 2, pb: 1 }}>
          <TextField
            size="small"
            fullWidth
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Filter sections"
            inputProps={{ 'aria-label': 'Filter table of contents' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      )}

      <Box sx={{ overflowY: 'auto', flex: 1, px: 1, pb: 2 }}>{renderNodes(tree)}</Box>
    </Box>
  );
});

export default TableOfContents;
